import { useState } from "react";
import { Link } from "react-router-dom";
import { insightArticles } from "../content";
import { ArrowIcon } from "../components/Icons";
import Seo from "../components/Seo";
import { assetUrl } from "../asset-url";

const pages = {
  insights: {
    title: "Insights that advance life science.",
    lead: "Research perspectives, platform explainers, application thinking, and evidence frameworks from across Medein.",
    image: assetUrl("/assets/optimized/insights-team.webp"),
  },
  about: {
    title: "Science with purpose. Impact for life.",
    lead: "Medein is a platform-driven life-science company connecting data, biology, precision systems, products, and partnerships.",
    image: assetUrl("/assets/optimized/lab-branded.webp"),
  },
  careers: {
    title: "Build the future of programmable life.",
    lead: "We are creating an interdisciplinary environment for science, engineering, design, and translation.",
    image: assetUrl("/assets/optimized/insights-team.webp"),
  },
  investors: {
    title: "A platform model for the future of life science.",
    lead: "Reusable science and platform capabilities connect focused applications, product lines, pipeline programs, and strategic partnerships.",
    image: assetUrl("/assets/optimized/hero-data-wave.webp"),
  },
  partnership: {
    title: "Transform biological possibilities into scalable impact.",
    lead: "Medein partners across research, platform development, product validation, manufacturing, clinical translation, and strategic growth.",
    image: assetUrl("/assets/optimized/lab-data.webp"),
  },
  contact: {
    title: "Contact Medein.",
    lead: "Connect with the right Medein team for products, science, partnerships, media, careers, investor relations, or general inquiries.",
    image: assetUrl("/assets/optimized/chemistry-lab.webp"),
  },
};

export default function UtilityPage({ pageKey }) {
  const [submitted, setSubmitted] = useState(false);
  const page = pages[pageKey];
  const isFormPage = pageKey === "contact" || pageKey === "partnership";

  const submit = (event) => {
    event.preventDefault();
    setSubmitted(true);
  };

  return (
    <>
      <Seo title={page.title} description={page.lead} />
      <section className={`inner-hero utility-hero utility-hero-${pageKey}`} id="overview">
        <div className="inner-hero-copy">
          <p className="section-label">{pageKey}</p>
          <h1>{page.title}</h1>
          <p className="inner-lead">{page.lead}</p>
          {!isFormPage ? (
            <Link className="button button-primary" to="/partnership">Partner With Us <ArrowIcon /></Link>
          ) : null}
        </div>
        <div className="inner-hero-media">
          <img src={page.image} alt={`${page.title} — Medein`} fetchPriority="high" decoding="async" />
        </div>
      </section>

      {pageKey === "insights" ? (
        <>
          <section className="section content-intro-grid">
            <div>
              <p className="section-label">Editorial system</p>
              <h2>Context before conclusions.</h2>
            </div>
            <p>
              Medein Insights separates perspective, public evidence, program status, and controlled technical
              material. Each article presents key takeaways and limitations without overstating readiness.
            </p>
          </section>
          <nav className="section subpage-links" aria-label="Insights sections">
            <Link to="/insights/articles">Articles <ArrowIcon /></Link>
            <Link to="/insights/resources">Resources <ArrowIcon /></Link>
            <Link to="/insights/events">Events <ArrowIcon /></Link>
          </nav>
          <section className="section insights-newsroom" id="articles">
            <article className="insights-feature">
              <img src={insightArticles[0].image} alt="" loading="lazy" decoding="async" />
              <div>
                <span>{insightArticles[0].category}</span>
                <h2>{insightArticles[0].title}</h2>
                <p>{insightArticles[0].summary}</p>
                <Link className="text-link" to={`/insights/articles/${insightArticles[0].slug}`}>
                  Read featured insight <ArrowIcon />
                </Link>
              </div>
            </article>
            <aside className="insights-story-rail">
              <p className="section-label">More from Medein</p>
              {insightArticles.slice(1).map((item) => (
                <article key={item.title}>
                  <span>{item.category}</span>
                  <h3>{item.title}</h3>
                  <p>{item.summary}</p>
                  <Link className="text-link" to={`/insights/articles/${item.slug}`}>
                    Read insight <ArrowIcon />
                  </Link>
                </article>
              ))}
            </aside>
          </section>
          <section className="newsletter-band">
            <div>
              <p className="section-label">Updates</p>
              <h2>Follow evidence, platform, and portfolio progress.</h2>
            </div>
            <Link className="button button-light" to="/contact">Request updates <ArrowIcon /></Link>
          </section>
        </>
      ) : null}

      {pageKey === "about" ? (
        <>
          <section className="section editorial-copy">
            <div>
              <p className="section-label">Who we are</p>
              <h2>Make complex biology more designable, measurable, and accountable.</h2>
            </div>
            <div>
              <p>
                Medein works at the intersection of biological systems, design intelligence, precision assays,
                products, and translational execution.
              </p>
              <p>
                Our shared architecture helps science and product programs progress with clear evidence,
                explicit limitations, responsible access, and reusable interfaces.
              </p>
            </div>
          </section>
          <section className="section context-section">
            <div className="section-heading split-heading">
              <div>
                <p className="section-label">Vision and mission</p>
                <h2>Programmable life. Precision science.</h2>
              </div>
              <p>
                Our vision is a life-science ecosystem where biological complexity can be approached through
                connected, evidence-aware systems. Our mission is to translate that architecture into focused
                research, products, and partnerships.
              </p>
            </div>
            <div className="context-grid">
              {[
                ["Precision", "Define the decision, measurement, and limitation clearly."],
                ["Trust", "Make evidence, status, ownership, and access visible."],
                ["Innovation", "Connect biological insight with design and engineering."],
                ["Scalability", "Build reusable modules without losing program context."],
              ].map(([title, text], index) => (
                <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </section>
          <section className="overview-flow">
            <div>
              <p className="section-label">Medein ecosystem</p>
              <h2>From scientific questions to coherent systems.</h2>
            </div>
            <ol>
              {["Science", "Platform", "Applications", "Product Line", "Pipeline"].map((step, index) => (
                <li key={step}><span>{String(index + 1).padStart(2, "0")}</span><strong>{step}</strong></li>
              ))}
            </ol>
          </section>
          <nav className="section subpage-links" aria-label="About sections">
            <Link to="/about/story">Our Story <ArrowIcon /></Link>
            <Link to="/about/leadership">Leadership <ArrowIcon /></Link>
            <Link to="/partnership">Partner With Us <ArrowIcon /></Link>
          </nav>
        </>
      ) : null}

      {pageKey === "careers" ? (
        <>
          <section className="section context-section">
            <div className="section-heading split-heading">
              <div><p className="section-label">Why Medein</p><h2>Work across disciplines, not inside silos.</h2></div>
              <p>Our programs bring together biology, data and AI, laboratory systems, product development, operations, and partnerships.</p>
            </div>
            <div className="context-grid">
              {[
                ["Science", "Biology, assay design, and translational evidence."],
                ["Data & AI", "Context models, scientific software, and decision systems."],
                ["Lab & Quality", "Precision experimentation, controls, and traceability."],
                ["Product & Operations", "Portfolio, manufacturing, partnerships, and execution."],
              ].map(([title, text], index) => (
                <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </section>
          <section className="process-section">
            <div className="process-heading">
              <p className="section-label">Hiring process</p>
              <h2>A clear, role-relevant conversation.</h2>
              <p>Steps may vary by role, but candidates are told what to expect and how information will be used.</p>
            </div>
            <ol className="process-steps">
              {["Apply", "Introductory screen", "Team interview", "Role-relevant discussion", "Decision"].map((step, index) => (
                <li key={step}><span>{String(index + 1).padStart(2, "0")}</span><strong>{step}</strong></li>
              ))}
            </ol>
          </section>
          <section className="section job-section">
            <div><p className="section-label">Open roles</p><h2>Current opportunities</h2></div>
            <div className="empty-jobs">
              <p>No public openings are listed at this time.</p>
              <p>Join the talent network and indicate the discipline, experience, and location context most relevant to you.</p>
              <Link className="button button-secondary" to="/contact">Join talent network <ArrowIcon /></Link>
            </div>
          </section>
        </>
      ) : null}

      {pageKey === "investors" ? (
        <>
          <section className="section investor-pillars">
            {[
              ["Reusable science", "Foundational capabilities support multiple focused programs."],
              ["Platform architecture", "Design, simulation, assays, and evidence share governed interfaces."],
              ["Focused portfolio", "Applications organize product concepts and pipeline opportunities."],
              ["Partnership model", "Collaboration extends validation, execution, and market understanding."],
            ].map(([title, text], index) => (
              <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h2>{title}</h2><p>{text}</p></article>
            ))}
          </section>
          <section className="overview-flow">
            <div><p className="section-label">Portfolio map</p><h2>A connected platform model.</h2></div>
            <ol>
              {["Science", "Platform", "Applications", "Product Line", "Pipeline"].map((step, index) => (
                <li key={step}><span>{String(index + 1).padStart(2, "0")}</span><strong>{step}</strong></li>
              ))}
            </ol>
          </section>
          <section className="governance-callout">
            <div><p className="section-label">Investor information</p><h2>Controlled documents, responsible disclosure.</h2></div>
            <p>Investor materials, financial information, governance documents, and forward-looking statements are shared only after review and appropriate access qualification.</p>
            <Link className="text-link" to="/contact">Contact Investor Relations <ArrowIcon /></Link>
          </section>
        </>
      ) : null}

      {pageKey === "partnership" ? (
        <>
          <section className="section context-section">
            <div className="section-heading split-heading">
              <div><p className="section-label">Partner types</p><h2>Different partners, one structured path.</h2></div>
              <p>Research institutions, biotechnology and pharmaceutical teams, clinical collaborators, industrial organizations, public-sector groups, and strategic investors can begin with a non-confidential inquiry.</p>
            </div>
            <div className="context-grid">
              {[
                ["Research", "Scientific questions, models, assays, and evidence programs."],
                ["Platform", "Module integration, pilots, and co-development."],
                ["Product", "Validation, supply, manufacturing, and regional pathways."],
                ["Strategic", "Portfolio, venture, and long-term ecosystem collaboration."],
              ].map(([title, text], index) => (
                <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </section>
          <section className="process-section">
            <div className="process-heading">
              <p className="section-label">Engagement process</p>
              <h2>From fit to governed execution.</h2>
              <p>Begin with non-confidential context. Sensitive information enters only after qualification and approved agreements.</p>
            </div>
            <ol className="process-steps">
              {["Qualify", "NDA", "Scope", "Diligence", "Execute"].map((step, index) => (
                <li key={step}><span>{String(index + 1).padStart(2, "0")}</span><strong>{step}</strong></li>
              ))}
            </ol>
          </section>
        </>
      ) : null}

      {pageKey === "contact" ? (
        <section className="section context-section">
          <div className="section-heading split-heading">
            <div><p className="section-label">Contact routing</p><h2>Reach the team that owns the next step.</h2></div>
            <p>Choose the closest category. General forms should contain no confidential research, patient, personal health, security-sensitive, or controlled technical information.</p>
          </div>
          <div className="context-grid">
            {[
              ["Products", "Specifications, status, availability, and supported-use questions."],
              ["Science", "Technology, evidence, and research collaboration."],
              ["Partnerships", "Co-development, validation, manufacturing, and strategic programs."],
              ["Company", "Investor relations, media, careers, and general contact."],
            ].map(([title, text], index) => (
              <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>
            ))}
          </div>
        </section>
      ) : null}

      {isFormPage ? (
        <section className="section form-section">
          <div className="form-intro">
            <p className="section-label">{pageKey === "partnership" ? "Partnership inquiry" : "Contact Medein"}</p>
            <h2>{submitted ? "Thank you." : "Tell us where you want to begin."}</h2>
            <p>
              {submitted
                ? "Your inquiry has been captured in this demonstration. Connect the form to your approved CRM or CMS workflow for production."
                : "Choose the closest topic and share enough context for the right team to respond."}
            </p>
          </div>
          {submitted ? (
            <div className="form-success" role="status">
              <img src={assetUrl("/assets/science_icon_svg_018_shield_check.svg")} alt="" />
              <h3>Inquiry recorded</h3>
              <p>No data was transmitted from this local demonstration.</p>
              <button className="button button-secondary" onClick={() => setSubmitted(false)}>Send another inquiry</button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={submit}>
              <label>
                Name
                <input name="name" autoComplete="name" required />
              </label>
              <label>
                Work email
                <input name="email" type="email" autoComplete="email" required />
              </label>
              <label>
                Organization
                <input name="organization" autoComplete="organization" required />
              </label>
              <label>
                Topic
                <select name="topic" defaultValue="" required>
                  <option value="" disabled>Select a topic</option>
                  <option>Research collaboration</option>
                  <option>Product or program</option>
                  <option>Scientific evidence</option>
                  <option>Investor relations</option>
                  <option>Careers</option>
                  <option>Media and general contact</option>
                </select>
              </label>
              <label className="full-field">
                How can we help?
                <textarea name="message" rows="5" required />
              </label>
              <label className="checkbox-field full-field">
                <input type="checkbox" required />
                <span>I understand this form does not accept confidential, patient, or sensitive technical information.</span>
              </label>
              <button className="button button-primary" type="submit">Submit inquiry <ArrowIcon /></button>
            </form>
          )}
        </section>
      ) : null}
    </>
  );
}
